import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Footer from './Footer';
import ResumeBuilderStudio from './ResumeBuilderStudio';
import './ResumeBuilderLanding.css';

const HERO_IMG = '/images/resume-builder/hero-floating-resume.png';
const BRAIN_IMG = '/images/resume-builder/ai-neural-brain.png';
const TEMPLATES_IMG = '/images/resume-builder/templates-stack.png';
const PORTAL_IMG = '/images/resume-builder/career-success-portal.png';

/* ---------- Three.js neural-particle hero canvas ---------- */
const NeuralHeroCanvas = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 60;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Particle field
    const particleCount = 280;
    const positions = new Float32Array(particleCount * 3);
    const speeds = new Float32Array(particleCount);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 140;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 90;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 60;
      speeds[i] = 0.05 + Math.random() * 0.15;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.3, 'rgba(224,163,255,0.9)');
      g.addColorStop(0.7, 'rgba(147,112,219,0.3)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    const material = new THREE.PointsMaterial({
      size: 1.6,
      map: sprite,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // Central neural orb
    const orbGeo = new THREE.IcosahedronGeometry(8, 1);
    const orbMat = new THREE.MeshBasicMaterial({
      color: 0xe0a3ff,
      wireframe: true,
      transparent: true,
      opacity: 0.35,
    });
    const orb = new THREE.Mesh(orbGeo, orbMat);
    scene.add(orb);

    const orb2 = new THREE.Mesh(
      new THREE.IcosahedronGeometry(11, 1),
      new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        wireframe: true,
        transparent: true,
        opacity: 0.18,
      }),
    );
    scene.add(orb2);

    let raf;
    let mx = 0;
    let my = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      const pos = geometry.attributes.position.array;
      for (let i = 0; i < particleCount; i++) {
        pos[i * 3 + 1] += speeds[i] * 0.4;
        if (pos[i * 3 + 1] > 45) pos[i * 3 + 1] = -45;
      }
      geometry.attributes.position.needsUpdate = true;

      orb.rotation.x += 0.003;
      orb.rotation.y += 0.005;
      orb2.rotation.x -= 0.002;
      orb2.rotation.y -= 0.003;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      geometry.dispose();
      material.dispose();
      orbGeo.dispose();
      orbMat.dispose();
      orb2.geometry.dispose();
      orb2.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="rbl-hero-canvas" aria-hidden="true" />;
};

/* ---------- ATS X-ray scanner over mock resume ---------- */
const AtsXrayDemo = () => {
  const sectionRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setScanning(true);
        });
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(() => {
      setRevealed((r) => (r >= 5 ? r : r + 1));
    }, 600);
    return () => clearInterval(t);
  }, [scanning]);

  const keywords = ['React', 'Node.js', 'TypeScript', 'AWS', 'CI/CD'];

  return (
    <section className="rbl-section rbl-ats" ref={sectionRef}>
      <div className="rbl-section-grid">
        <div className="rbl-section-text">
          <span className="rbl-eyebrow">ATS X-RAY ENGINE</span>
          <h2 className="rbl-h2">
            See your resume <span className="rbl-grad-text">through the recruiter's scanner</span>
          </h2>
          <p className="rbl-lead">
            Most resumes get filtered out by ATS before a human ever sees them. We scan yours the same
            way they do — keyword density, section structure, parseable formatting — and surface every
            gap in real time.
          </p>
          <ul className="rbl-bullet-list">
            <li><span className="rbl-dot" />Keyword & skill coverage vs. job description</li>
            <li><span className="rbl-dot" />Section ordering parsers actually understand</li>
            <li><span className="rbl-dot" />Format traps: tables, columns, headers, icons</li>
            <li><span className="rbl-dot" />Live score that updates as you edit</li>
          </ul>
        </div>

        <div className="rbl-ats-stage">
          <div className="rbl-ats-glow" />
          <div className="rbl-ats-paper">
            <div className="rbl-ats-row rbl-ats-name" />
            <div className="rbl-ats-row rbl-ats-sub" />
            <div className="rbl-ats-row rbl-ats-line" />
            <div className="rbl-ats-row rbl-ats-line short" />
            <div className="rbl-ats-row rbl-ats-line" />
            <div className="rbl-ats-keywords">
              {keywords.map((k, i) => (
                <span
                  key={k}
                  className={`rbl-ats-kw ${i < revealed ? 'lit' : ''}`}
                  style={{ animationDelay: `${i * 120}ms` }}
                >
                  {k}
                </span>
              ))}
            </div>
            <div className="rbl-ats-row rbl-ats-line" />
            <div className="rbl-ats-row rbl-ats-line short" />
            <div className="rbl-ats-row rbl-ats-line" />
          </div>
          <div className={`rbl-ats-beam ${scanning ? 'on' : ''}`} />
          <div className="rbl-ats-grid" />
          <div className="rbl-ats-score">
            <span className="rbl-ats-score-val">{75 + revealed * 4}</span>
            <span className="rbl-ats-score-lbl">ATS Match</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Template morph carousel ---------- */
const TEMPLATE_DEFS = [
  { name: 'Professional', accent: '#e0a3ff', layout: 'pro' },
  { name: 'Modern', accent: '#00ffff', layout: 'modern' },
  { name: 'Creative', accent: '#ff69b4', layout: 'creative' },
  { name: 'Academic', accent: '#9370db', layout: 'academic' },
];

const TemplateMorph = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TEMPLATE_DEFS.length), 2800);
    return () => clearInterval(t);
  }, []);

  const t = TEMPLATE_DEFS[idx];
  return (
    <section className="rbl-section rbl-templates">
      <div className="rbl-section-grid rbl-flip">
        <div className="rbl-template-stage">
          <div className="rbl-template-glow" style={{ background: `radial-gradient(circle, ${t.accent}33 0%, transparent 70%)` }} />
          {TEMPLATE_DEFS.map((tpl, i) => {
            const offset = (i - idx + TEMPLATE_DEFS.length) % TEMPLATE_DEFS.length;
            return (
              <div
                key={tpl.name}
                className={`rbl-template-card layout-${tpl.layout}`}
                style={{
                  '--accent': tpl.accent,
                  transform: `translate(${offset * 14}px, ${offset * 18}px) rotate(${offset * 2.5}deg) scale(${1 - offset * 0.04})`,
                  zIndex: TEMPLATE_DEFS.length - offset,
                  opacity: offset === 0 ? 1 : 0.55 - offset * 0.12,
                }}
              >
                <div className="rbl-template-head" />
                <div className="rbl-template-body">
                  <div className="rbl-template-bar w90" />
                  <div className="rbl-template-bar w70" />
                  <div className="rbl-template-bar w80" />
                  <div className="rbl-template-bar w50" />
                  <div className="rbl-template-bar w85" />
                  <div className="rbl-template-bar w60" />
                </div>
              </div>
            );
          })}
        </div>

        <div className="rbl-section-text">
          <span className="rbl-eyebrow">LIVE TEMPLATE MORPH</span>
          <h2 className="rbl-h2">
            Four hand-tuned templates. <span className="rbl-grad-text">One click to switch.</span>
          </h2>
          <p className="rbl-lead">
            Every template is built for ATS parseability first, beauty second. Switch styles
            instantly — your content stays put, only the layout breathes.
          </p>
          <div className="rbl-template-pills">
            {TEMPLATE_DEFS.map((tpl, i) => (
              <button
                key={tpl.name}
                type="button"
                className={`rbl-pill ${i === idx ? 'active' : ''}`}
                style={{ '--accent': tpl.accent }}
                onClick={() => setIdx(i)}
              >
                {tpl.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- AI typing live preview ---------- */
const AI_BEFORE = 'Worked on the backend team and helped with API stuff and bug fixes.';
const AI_AFTER =
  'Architected and shipped 12 production REST endpoints in Node.js, cutting p95 latency 38% and unblocking 3 downstream teams.';

const AiTypingPreview = () => {
  const sectionRef = useRef(null);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | typing | done

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && phase === 'idle') setPhase('typing');
        });
      },
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'typing') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(AI_AFTER.slice(0, i));
      if (i >= AI_AFTER.length) {
        clearInterval(id);
        setPhase('done');
      }
    }, 22);
    return () => clearInterval(id);
  }, [phase]);

  return (
    <section className="rbl-section rbl-aityping" ref={sectionRef}>
      <div className="rbl-section-grid">
        <div className="rbl-section-text">
          <span className="rbl-eyebrow">AI REWRITE LIVE</span>
          <h2 className="rbl-h2">
            From flat to <span className="rbl-grad-text">recruiter-magnet</span> — in real time.
          </h2>
          <p className="rbl-lead">
            Paste a weak bullet. Watch our model rewrite it with action verbs, quantified impact,
            and the keywords your target role actually screens for.
          </p>
        </div>

        <div className="rbl-rewrite-card">
          <div className="rbl-rewrite-block">
            <div className="rbl-rewrite-label rbl-tag-red">Before</div>
            <p className="rbl-rewrite-before">{AI_BEFORE}</p>
          </div>

          <div className="rbl-rewrite-arrow">
            <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M12 4l1.41 1.41L7.83 11H20v2H7.83l5.58 5.59L12 20l-8-8z" transform="rotate(180 12 12)"/></svg>
          </div>

          <div className="rbl-rewrite-block rbl-rewrite-after-block">
            <div className="rbl-rewrite-label rbl-tag-green">After (AI rewrite)</div>
            <p className="rbl-rewrite-after">
              {typed}
              {phase === 'typing' && <span className="rbl-caret">|</span>}
            </p>
            {phase === 'done' && (
              <div className="rbl-rewrite-meta">
                <span>+38% latency cut</span>
                <span>3 keywords matched</span>
                <span>STAR-format ✓</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Neural-network process flow ---------- */
const PROCESS_STEPS = [
  { title: 'Import or start fresh', body: 'Upload PDF / DOCX or build from a blank canvas.' },
  { title: 'AI parses your story', body: 'We extract roles, achievements, and skills with context.' },
  { title: 'Smart bullet rewrites', body: 'Action verbs, quantified impact, role-relevant keywords.' },
  { title: 'ATS structure applied', body: 'Section order and formatting parsers actually understand.' },
  { title: 'Tailor for each job', body: 'Drop a JD — we adjust emphasis, not your truth.' },
  { title: 'Export & track', body: 'PDF, DOCX, shareable link — and a live ATS score.' },
];

const NeuralFlow = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(0); });
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (active < 0) return;
    if (active >= PROCESS_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="rbl-section rbl-flow" ref={ref}>
      <div className="rbl-section-head">
        <span className="rbl-eyebrow">HOW IT WORKS</span>
        <h2 className="rbl-h2">
          Six steps. <span className="rbl-grad-text">One neural pipeline.</span>
        </h2>
        <p className="rbl-lead rbl-center">
          Every step lights up as it runs — nothing is hidden, nothing is hallucinated.
        </p>
      </div>

      <div className="rbl-flow-grid">
        {PROCESS_STEPS.map((s, i) => (
          <div key={s.title} className={`rbl-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="rbl-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < PROCESS_STEPS.length - 1 && <div className={`rbl-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Parallax stat counters ---------- */
const STATS = [
  { val: 95, suffix: '%', label: 'ATS pass rate' },
  { val: 10, suffix: 'x', label: 'Faster than hand-editing' },
  { val: 4, suffix: '', label: 'Hand-tuned templates' },
  { val: 500, suffix: 'K+', label: 'Resumes built' },
];

const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};

const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="rbl-stat">
      <div className="rbl-stat-num">{v}{stat.suffix}</div>
      <div className="rbl-stat-lbl">{stat.label}</div>
    </div>
  );
};

const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setStart(true); });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="rbl-stats-band" ref={ref}>
      <div className="rbl-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- Glass FAQ ---------- */
const FAQS = [
  { q: 'Will recruiters know my resume was AI-built?', a: 'No. Our model improves clarity and impact in your voice — it never substitutes its words for yours, and you approve every change.' },
  { q: 'Can the AI invent experience I don\'t have?', a: 'Never. We only ever rewrite what you provide. Nothing is fabricated, no fake metrics, no invented roles.' },
  { q: 'Is my data safe?', a: 'Your resume is encrypted in transit and at rest. We don\'t train models on your data. You can delete everything at any time.' },
  { q: 'Will my resume look like everyone else\'s?', a: 'Four hand-tuned templates × infinite content variations × your tailoring per job means no two resumes ever come out the same.' },
  { q: 'Does it actually beat ATS?', a: 'Yes — measured on 5 of the most common ATS engines. Our parseable layouts and keyword logic push pass rates past 95%.' },
  { q: 'How much does it cost?', a: 'The builder is free. Premium templates and unlimited AI rewrites are part of the paid plans — check Pricing for the latest.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="rbl-section rbl-faq">
      <div className="rbl-section-head">
        <span className="rbl-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="rbl-h2">Everything you wanted to ask</h2>
      </div>
      <div className="rbl-faq-list">
        {FAQS.map((f, i) => (
          <button
            key={f.q}
            type="button"
            className={`rbl-faq-item ${open === i ? 'open' : ''}`}
            onClick={() => setOpen(open === i ? -1 : i)}
          >
            <div className="rbl-faq-q">
              <span>{f.q}</span>
              <span className="rbl-faq-chev">+</span>
            </div>
            <div className="rbl-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const ResumeBuilderLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  const goBuild = useCallback(() => {
    if (studioRef.current) {
      studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  const goATS = useCallback(() => navigate('/ATSCheck'), [navigate]);

  // Auth modal lives on the homepage; route there with a flag when needed.
  const setShowAuthModal = useCallback((show) => {
    if (show) navigate('/?openLogin=1');
  }, [navigate]);

  // Hero parallax
  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="rbl-page">
      <Header setShowAuthModal={setShowAuthModal} />

      <section className="rbl-hero" ref={heroRef}>
        <NeuralHeroCanvas />
        <div className="rbl-hero-bg" />
        <img src={HERO_IMG} alt="" className="rbl-hero-img" loading="eager" />

        <div className="rbl-hero-content">
          <span className="rbl-hero-pill">
            <span className="rbl-pill-dot" /> Premium · ATS-ready in minutes
          </span>
          <h1 className="rbl-h1">
            Build a resume <span className="rbl-grad-text">recruiters can't ignore</span>.
          </h1>
          <p className="rbl-hero-sub">
            500K+ resumes built. We blend a neural rewriter, an ATS X-ray engine, and four
            hand-tuned templates — so your story lands first, not gets filtered out.
          </p>

          <div className="rbl-hero-cta">
            <button className="rbl-btn rbl-btn-primary" onClick={goBuild}>
              <span>Start building free</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="rbl-btn rbl-btn-ghost" onClick={goATS}>Check ATS Score</button>
          </div>

          <div className="rbl-hero-checks">
            <span>✓ ATS-Optimized</span>
            <span>✓ AI-Powered</span>
            <span>✓ Free to Start</span>
          </div>
        </div>

        {/* Floating CSS-3D resume page */}
        <div className="rbl-floater" aria-hidden="true">
          <div className="rbl-floater-page">
            <div className="rbl-fp-band" />
            <div className="rbl-fp-line w80" />
            <div className="rbl-fp-line w60" />
            <div className="rbl-fp-line w90" />
            <div className="rbl-fp-line w70" />
            <div className="rbl-fp-line w85" />
            <div className="rbl-fp-line w55" />
            <div className="rbl-fp-line w75" />
          </div>
          <div className="rbl-floater-glow" />
        </div>
      </section>

      <div ref={studioRef}>
        <ResumeBuilderStudio setShowAuthModal={setShowAuthModal} />
      </div>

      <AtsXrayDemo />

      <section className="rbl-section rbl-feat-row">
        <div className="rbl-section-grid rbl-flip">
          <div className="rbl-img-frame">
            <img src={BRAIN_IMG} alt="AI neural brain processing resume content" />
            <div className="rbl-img-frame-glow" />
          </div>
          <div className="rbl-section-text">
            <span className="rbl-eyebrow">NEURAL REWRITER</span>
            <h2 className="rbl-h2">
              An AI trained on what <span className="rbl-grad-text">actually gets hired</span>.
            </h2>
            <p className="rbl-lead">
              Our model studies how senior recruiters and hiring managers read resumes — what they
              skip, what they zoom on, what makes them pick up the phone. Every rewrite leans on
              that pattern.
            </p>
            <ul className="rbl-bullet-list">
              <li><span className="rbl-dot" />Action verbs & quantified outcomes</li>
              <li><span className="rbl-dot" />Role-specific keyword targeting</li>
              <li><span className="rbl-dot" />Voice preserved — never generic</li>
            </ul>
          </div>
        </div>
      </section>

      <TemplateMorph />
      <AiTypingPreview />
      <NeuralFlow />

      <section className="rbl-section rbl-feat-row">
        <div className="rbl-section-grid">
          <div className="rbl-section-text">
            <span className="rbl-eyebrow">CAREER TRAJECTORY</span>
            <h2 className="rbl-h2">
              A resume isn't a document. <span className="rbl-grad-text">It's a launchpad.</span>
            </h2>
            <p className="rbl-lead">
              We track which versions land interviews and which stall — so the next version you
              ship learns from the last one. Your resume gets sharper every time you apply.
            </p>
            <button className="rbl-btn rbl-btn-primary" onClick={goBuild}>
              <span>Launch the builder</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
          </div>
          <div className="rbl-img-frame">
            <img src={PORTAL_IMG} alt="Career success portal of light" />
            <div className="rbl-img-frame-glow" />
          </div>
        </div>
      </section>

      <StatsBand />

      <section className="rbl-section rbl-feat-row">
        <div className="rbl-section-grid rbl-flip">
          <div className="rbl-img-frame">
            <img src={TEMPLATES_IMG} alt="Layered holographic resume templates" />
            <div className="rbl-img-frame-glow" />
          </div>
          <div className="rbl-section-text">
            <span className="rbl-eyebrow">YOU STAY IN CONTROL</span>
            <h2 className="rbl-h2">
              Every suggestion is <span className="rbl-grad-text">optional</span>.
            </h2>
            <p className="rbl-lead">
              Accept, reject, or rewrite — your resume always reflects your real experience.
              No black-box AI. No silent edits. No surprises in the export.
            </p>
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="rbl-cta-final">
        <div className="rbl-cta-card">
          <div className="rbl-cta-glow" />
          <h2 className="rbl-h2">
            Build your AI-optimized resume <span className="rbl-grad-text">for free</span>.
          </h2>
          <p>Jump into the builder and ship a clean, ATS-ready resume in minutes.</p>
          <div className="rbl-hero-cta center">
            <button className="rbl-btn rbl-btn-primary" onClick={goBuild}>
              <span>Build your resume now</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="rbl-btn rbl-btn-ghost" onClick={goATS}>Check ATS Score</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResumeBuilderLanding;
